import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatPrice, formatDate, formatCountdown } from '../utils/format';

export interface OrderLine {
  id: number;
  title: string;
  sku: string | null;
  quantity: number;
  price: string;
  line_total: string | number;
  refunded_quantity: number;
  book_isbn_13?: string | null;
  book_cover_image_url?: string | null;
}

export interface OrderDetail {
  id: number;
  order_number: string | null;
  vendor_name?: string | null;
  customer_name: string | null;
  customer_phone?: string | null;
  shipping_address?: string | null;
  shipping_city: string | null;
  currency: string;
  subtotal: string;
  status: 'open' | 'cancelled' | 'refunded';
  confirmation_status: 'pending' | 'confirmed' | 'declined' | 'expired';
  confirmation_deadline: string | null;
  confirmed_at?: string | null;
  decline_reason?: string | null;
  seconds_to_deadline: number | null;
  received_at: string;
  lines?: OrderLine[];
  items_count?: number;
}

const STATUS_STYLE: Record<string, string> = {
  pending: 'bg-orange-100 text-orange-800',
  confirmed: 'bg-green-100 text-green-800',
  declined: 'bg-red-100 text-red-800',
  expired: 'bg-gray-200 text-gray-700',
  cancelled: 'bg-gray-200 text-gray-700',
  refunded: 'bg-gray-200 text-gray-700',
};

export const OrderStatusBadge: React.FC<{ order: OrderDetail }> = ({ order }) => {
  const { t } = useTranslation();
  const key = order.status !== 'open' ? order.status : order.confirmation_status;
  const label =
    key === 'pending' ? t('orders.pendingConfirmation') : t(`orders.${key}`);
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLE[key] || STATUS_STYLE.expired}`}>
      {label}
    </span>
  );
};

export const Countdown: React.FC<{ seconds: number | null; deadline: string | null }> = ({ seconds, deadline }) => {
  const { t } = useTranslation();
  const [left, setLeft] = useState<number>(() => {
    if (deadline) return Math.max(0, Math.floor((new Date(deadline).getTime() - Date.now()) / 1000));
    return seconds ?? 0;
  });
  useEffect(() => {
    const id = window.setInterval(() => {
      setLeft(deadline ? Math.max(0, Math.floor((new Date(deadline).getTime() - Date.now()) / 1000)) : (s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [deadline]);
  const urgent = left < 5 * 60;
  return (
    <span className={`font-mono text-sm font-semibold ${urgent ? 'text-red-600' : 'text-orange-700'}`}>
      {t('orders.expiresIn', { time: formatCountdown(left) })}
    </span>
  );
};

interface Props {
  order: OrderDetail;
  showVendor?: boolean;
  onRespond?: (order: OrderDetail, action: 'confirm' | 'decline', reason?: string) => Promise<void>;
  /** Public (token) page: hide contact details the seller can see when logged in */
  compact?: boolean;
}

const OrderCard: React.FC<Props> = ({ order, showVendor, onRespond, compact }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<'confirm' | 'decline' | null>(null);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');

  const awaiting = order.status === 'open' && order.confirmation_status === 'pending';

  const act = async (action: 'confirm' | 'decline') => {
    if (!onRespond) return;
    setBusy(action);
    try {
      await onRespond(order, action, reason);
      setDeclining(false);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={`bg-white shadow rounded-lg overflow-hidden ${awaiting ? 'ring-2 ring-orange-300' : ''}`}>
      <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-base font-semibold text-gray-900">
            {t('orders.order')} {order.order_number || `#${order.id}`}
          </p>
          <p className="text-xs text-gray-500">
            {t('orders.received')} {formatDate(order.received_at)}
            {showVendor && (
              <>
                {' · '}
                {t('orders.vendor')}: {order.vendor_name || t('orders.unassigned')}
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {awaiting && <Countdown seconds={order.seconds_to_deadline} deadline={order.confirmation_deadline} />}
          <OrderStatusBadge order={order} />
        </div>
      </div>

      <div className="px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-gray-500">{t('orders.customer')}</p>
          <p className="text-gray-900">{order.customer_name || '—'}</p>
          {!compact && order.customer_phone && <p className="text-gray-700">{order.customer_phone}</p>}
        </div>
        <div>
          <p className="text-gray-500">{t('orders.deliverTo')}</p>
          <p className="text-gray-900">{compact ? order.shipping_city || '—' : order.shipping_address || order.shipping_city || '—'}</p>
        </div>
      </div>

      {order.lines && order.lines.length > 0 && (
        <ul className="px-4 divide-y divide-gray-100 border-t border-gray-100">
          {order.lines.map((line) => (
            <li key={line.id} className="py-2 flex items-center gap-3">
              <div className="w-9 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                {line.book_cover_image_url && (
                  <img src={line.book_cover_image_url} alt="" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900 truncate">{line.title}</p>
                {line.book_isbn_13 && <p className="text-xs text-gray-500">ISBN {line.book_isbn_13}</p>}
              </div>
              <div className="text-sm text-gray-700 whitespace-nowrap">
                {line.quantity} × {formatPrice(line.price)}
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="px-4 py-3 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm">
          <span className="text-gray-500">{t('orders.total')}: </span>
          <span className="font-semibold text-gray-900">{formatPrice(order.subtotal)}</span>
        </p>
        {order.confirmation_status === 'declined' && order.decline_reason && (
          <p className="text-xs text-gray-500">{t('orders.declineReason')}: {order.decline_reason}</p>
        )}
        {awaiting && onRespond && !declining && (
          <div className="flex gap-2">
            <button
              onClick={() => setDeclining(true)}
              disabled={!!busy}
              className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 text-sm font-medium min-h-[44px] disabled:opacity-50"
            >
              {t('orders.declineOrder')}
            </button>
            <button
              onClick={() => act('confirm')}
              disabled={!!busy}
              className="px-4 py-2 rounded-md bg-green-600 text-white text-sm font-medium min-h-[44px] disabled:opacity-50"
            >
              {busy === 'confirm' ? t('orders.confirming') : t('orders.confirmOrder')}
            </button>
          </div>
        )}
      </div>

      {awaiting && declining && (
        <div className="px-4 pb-4 space-y-2">
          <label className="block text-sm text-gray-700">{t('orders.declineReason')}</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('orders.declineReasonPlaceholder')}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setDeclining(false)} className="px-4 py-2 rounded-md border border-gray-300 text-sm min-h-[40px]">{t('common.cancel')}</button>
            <button onClick={() => act('decline')} disabled={!!busy} className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-medium min-h-[40px] disabled:opacity-50">
              {busy === 'decline' ? t('orders.declining') : t('orders.declineOrder')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderCard;
